import { Modal } from 'antd';
import { Fragment, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
    GO_TO_APPOINTMENTS,
    GO_TO_PRESCRIPTION_PAD,
    PPRouterProps,
} from '../../../../../routes/paths';
import {
    getDentalExaminations,
    getDentalProcedures,
    getDiagnosis,
    getElementsFromPrescription,
    getInjections,
    getLabTests,
    getLabVitals,
    getMedications,
    getPrescription,
    getPrintConfigs,
    getSymptoms,
    getTranslate,
    getVitals,
} from '../../../../../store/selectors/prescriptionPad';
import { AntTooltip } from '../../../../common/AntTooltip';
import { PAD_V2_CONTAINER } from '../PadContainer';
import pushPxDraft from '../../../../../api/pushPxDraft';
import { createBETool } from '../../../../../api/pushVisit';
import { setToastType, updateDraftId } from '../../../../../store/actions/prescriptionPad';
import {
    getGenieSetting,
    getIsNotesMode,
    getPxElementConfig,
    getSectionNameConfig,
} from '../../../../../store/selectors/session';
import { setIsPageBlocked } from '../../../../../App';
import { setDontAllowStartVisit, setIsNotesMode } from '../../../../../store/actions/session';
import { GlobalState, GlobalStateV1 } from '../../../../../store/reducers';
import { Procedure } from '../../../../../store/types/prescription';
import { triggerPushVisit } from '../../finishScreen/FinishedPanel';
import { isForRxMonetisation } from '../../../../../pages/utils';
import { GeniePadElementsSettingItem } from '../../../../../api/pushGenie';
import { toTitleCase } from '../../../../../store/utils';

const FinishBt: React.FC = () => {
    const history = useHistory();
    const params: PPRouterProps = useParams();
    const sym = useSelector(getSymptoms);
    const dia = useSelector(getDiagnosis);
    const med = useSelector(getMedications);
    const injections = useSelector(getInjections);
    const lab = useSelector(getLabTests);
    const labVital = useSelector(getLabVitals);
    const vitals = useSelector(getVitals);
    const dentalExaminations = useSelector(getDentalExaminations);
    const dentalProcedures = useSelector(getDentalProcedures);
    const procedures = useSelector((state: GlobalState) =>
        getElementsFromPrescription<Procedure>('procedures')(state),
    );
    const isNotesMode = useSelector(getIsNotesMode);
    const px = useSelector(getPrescription);
    const dispatch = useDispatch();

    const exit = new URLSearchParams(history.location.search).get('exit');
    const action = new URLSearchParams(history.location.search).get('action');

    const [isPushUpdateLoading, setIsPushUpdateLoading] = useState<boolean>(false);

    const goToFinish = (print?: boolean) => {
        if (!params.pxId) {
            history.replace('/');
        }

        history.push(
            exit
                ? `${exit + '?force=true'}`
                : GO_TO_PRESCRIPTION_PAD(params.pxId, 'finish') + '?print=' + print,
        );
    };

    const pushToCloud = () => {
        if (!px) {
            return;
        }

        setIsPushUpdateLoading(true);
        pushPxDraft(px.id, px.visitId, createBETool(px)).then((r) => {
            batch(() => {
                if (r.success && r.draftId) {
                    dispatch(updateDraftId(r.draftId));
                    setIsPushUpdateLoading(false);
                }
            });
        });
    };

    const handleSaveNotesClick = async () => {
        await pushToCloud();
        gotToQueue();
    };

    const pc = useSelector(getPrintConfigs);
    const padElementsConfig = useSelector(getPxElementConfig);
    const sectionNameConfig = useSelector(getSectionNameConfig);
    const translate = useSelector(getTranslate);
    const medElements = useSelector((state: GlobalStateV1) =>
        getGenieSetting(state, 'medElements'),
    ) as GeniePadElementsSettingItem[];

    const onCreate = async (print: boolean): Promise<void> => {
        if (action === 'save') {
            if (!px) {
                return;
            }
            setIsPushUpdateLoading(true);
            await triggerPushVisit(
                px,
                pc,
                translate,
                undefined,
                undefined,
                undefined,
                (error) => {
                    if (error) {
                        alert(error.message);
                    }

                    goToFinish();
                },
                undefined,
                undefined,
                undefined,
                undefined,
                isForRxMonetisation(),
                medElements,
                padElementsConfig,
                sectionNameConfig,
            );
            return;
        }
        const con = document.getElementById(PAD_V2_CONTAINER);

        if (!con) {
            return;
        }

        const errors = con.querySelectorAll('.ant-form-item-explain-error');

        if (errors.length > 0) {
            Modal.confirm({
                title: `${errors.length} error(s) found`,
                content: 'Are you sure you want to continue?',
                okText: 'Continue',
                onOk: () => goToFinish(print),
                cancelText: 'Cancel',
                okButtonProps: { className: 'primary-btn font-lato' },
                cancelButtonProps: { className: 'neutral-btn font-lato' },
            });

            setTimeout(() => {
                const el: HTMLDivElement = document.getElementsByClassName(
                    'ant-modal-wrap',
                )[0] as HTMLDivElement;
                el?.focus();
            }, 200);
            return;
        }

        goToFinish(print);
    };

    const gotToQueue = () => {
        setIsPageBlocked(false);
        history.replace(
            GO_TO_APPOINTMENTS(px?.clinic._id || '', 'today-queue', false, 'left=booked'),
        );
    };

    const isInvalidAddedDrug = injections.reduce((acc, injection) => {
        const filteredDrug = injection.added_drug?.filter((drug) => !drug.id || !drug.name);

        return acc || !!(filteredDrug && filteredDrug.length > 0);
    }, false);

    const isD =
        [
            ...sym,
            ...dia,
            ...med,
            ...lab,
            ...labVital,
            ...vitals,
            ...dentalExaminations,
            ...dentalProcedures,
            ...injections,
            ...procedures,
        ].find((t) => !t.id || !t.name) !== undefined ||
        [...vitals].filter((v) => !v.value.qt).length > 0 ||
        isInvalidAddedDrug;

    const Wrapper = isD ? Fragment : AntTooltip;

    return (
        <div className="tooltip-wrapper space-x-8">
            {action || isNotesMode ? null : (
                <Wrapper content="Print & Finish Rx" mouseEnterDelay={0}>
                    <button
                        onClick={() => onCreate(true)}
                        className="px-16 py-8 btn--small secondary-btn-v2"
                        disabled={isD}
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-16 h-16 fill-white"
                                viewBox="0 0 512 512"
                            >
                                <path d="M128 0C92.7 0 64 28.7 64 64v96h64V64H354.7L384 93.3V160h64V93.3c0-17-6.7-33.3-18.7-45.3L400 18.7C388 6.7 371.7 0 354.7 0H128zM384 352v32 64H128V384 368 352H384zm64 32h32c17.7 0 32-14.3 32-32V256c0-35.3-28.7-64-64-64H64c-35.3 0-64 28.7-64 64v96c0 17.7 14.3 32 32 32H64v64c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V384zm-16-88c-13.3 0-24-10.7-24-24s10.7-24 24-24s24 10.7 24 24s-10.7 24-24 24z" />
                            </svg>
                        </div>
                    </button>
                </Wrapper>
            )}

            <button
                onClick={isNotesMode ? handleSaveNotesClick : () => onCreate(false)}
                disabled={isD}
                className="px-16 py-8 btn--small primary-btn-v2 tooltip-wrapper"
            >
                <div className="flex items-center justify-center w-16 h-16">
                    {isPushUpdateLoading ? (
                        <svg
                            className="w-16 h-16 fill-actionPrimary animate-spin"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                    ) : (
                        <svg
                            className="w-16 h-16 fill-actionPrimary"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                        >
                            <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                        </svg>
                    )}
                </div>
                <p>
                    {isNotesMode
                        ? 'Save Notes'
                        : action
                        ? toTitleCase(action)
                        : 'Finish Prescription'}
                </p>
            </button>
            {isD && (
                <div className="tooltip-tip top" style={{ top: -62 }}>
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <span>One of the Rx</span>
                        <span>element is empty</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinishBt;
